'use client';

import { DARK_MODE_COOKIE_NAME } from '@lib/constants/cookies';
import { failingFetcher } from '@lib/http/fetch';
import { applyDarkModeClass, DARK_MODE_API_PATH } from '@lib/theme/colorScheme';
import Cookie from 'js-cookie';
import { getSession, useSession } from 'next-auth/react';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type ColorSchemeContextType = {
  enabled: boolean;
  isDarkMode: boolean;
  setDarkMode: (isDarkMode: boolean) => void;
};

export const ColorSchemeContext = createContext<
  ColorSchemeContextType | undefined
>(undefined);

export const useColorScheme = () => {
  const context = useContext(ColorSchemeContext);
  if (!context) {
    throw new Error('useColorScheme must be used within a ColorSchemeProvider');
  }
  return context;
};

type ColorSchemeProviderProps = PropsWithChildren<{
  enabled: boolean;
  initialDarkMode?: boolean;
}>;

export const ColorSchemeProvider = ({
  enabled,
  initialDarkMode = false,
  children,
}: ColorSchemeProviderProps) => {
  const [isDarkMode, setIsDarkMode] = useState(enabled && initialDarkMode);
  const { status } = useSession();
  const pendingSave = useRef<Promise<void>>(Promise.resolve());
  // A sign-out in another tab reaches this one through the session broadcast only.
  const isEnabled = enabled && status !== 'unauthenticated';

  useEffect(() => {
    if (!isEnabled) {
      applyDarkModeClass(document.documentElement, false);
    }
  }, [isEnabled]);

  // A router.refresh() re-renders the root layout without remounting this provider.
  useEffect(() => {
    setIsDarkMode(enabled && initialDarkMode);
  }, [enabled, initialDarkMode]);

  const setDarkMode = useCallback(
    (next: boolean) => {
      if (!isEnabled) return;

      setIsDarkMode(next);
      applyDarkModeClass(document.documentElement, next);
      // Same attributes as asv2 writes: host-only, browser-session lifetime.
      Cookie.set(DARK_MODE_COOKIE_NAME, String(next), { path: '/' });

      // Queued so a slower earlier save cannot overwrite a later choice.
      pendingSave.current = pendingSave.current.then(async () => {
        await failingFetcher(DARK_MODE_API_PATH, {
          method: 'PUT',
          body: JSON.stringify({ darkMode: next }),
        }).catch((error) => console.error('Failed to save dark mode:', error));
        // The jwt callback copies AC_DARK into the token; no broadcast, no loading state.
        await getSession({ broadcast: false });
      });
    },
    [isEnabled]
  );

  const contextValue = useMemo<ColorSchemeContextType>(
    () => ({
      enabled: isEnabled,
      isDarkMode: isEnabled && isDarkMode,
      setDarkMode,
    }),
    [isEnabled, isDarkMode, setDarkMode]
  );

  return (
    <ColorSchemeContext.Provider value={contextValue}>
      {children}
    </ColorSchemeContext.Provider>
  );
};
